// ============================================================
// Deteccao de trafego automatizado (bots, crawlers, headless).
// Usado para NAO contabilizar "visitas fantasmas" no GA4 nem na
// captura first-party (public.page_views).
// ============================================================

const BOT_UA_PATTERNS = [
  "bot", "crawl", "spider", "slurp", "curl", "wget", "python-requests",
  "axios", "node-fetch", "okhttp", "java/", "go-http-client", "libwww",
  "httpclient", "scrapy", "phantomjs", "headlesschrome", "puppeteer",
  "playwright", "selenium", "lighthouse", "pagespeed", "gtmetrix",
  "pingdom", "uptimerobot", "ahrefs", "semrush", "mj12", "dotbot",
  "petalbot", "dataforseo", "seokicks", "serpstat", "screaming frog",
  "google-inspectiontool", "chrome-lighthouse", "preview", "monitor",
];

const HUMAN_VERIFIED_KEY = "ao_human_verified";
export const HUMAN_VERIFIED_EVENT = "ao:human-verified";
let verificationStarted = false;
let humanVerified = false;

/** Heuristicas de navegador automatizado (headless / webdriver). */
function hasAutomationSignals(): boolean {
  try {
    const nav = navigator as Navigator & { webdriver?: boolean };
    if (nav.webdriver === true) return true;
    // Headless classico: sem plugins, sem idiomas
    if (Array.isArray(nav.languages) && nav.languages.length === 0) return true;
    const w = window as unknown as Record<string, unknown>;
    if (w.__nightmare || w._phantom || w.callPhantom || w.__selenium_unwrapped) return true;
    // Viewport degenerado (headless sem janela real)
    if (window.outerWidth === 0 && window.outerHeight === 0) return true;
    return false;
  } catch {
    return false;
  }
}

function hasRealBrowserSignals(): boolean {
  try {
    return window.innerWidth >= 240
      && window.innerHeight >= 240
      && navigator.languages.length > 0;
  } catch {
    return false;
  }
}


/**
 * true quando a visita muito provavelmente NAO e de um humano.
 * Falha para "false" (conta a visita) em caso de duvida.
 */
export function isBotTraffic(): boolean {
  try {
    if (typeof window === "undefined" || typeof navigator === "undefined") return true;
    const ua = (navigator.userAgent || "").toLowerCase();
    if (!ua) return true;
    if (BOT_UA_PATTERNS.some((p) => ua.includes(p))) return true;
    if (hasAutomationSignals()) return true;
    return false;
  } catch {
    return false;
  }
}

/** A sessão só pode gerar analytics após atividade humana confiável. */
export function isHumanVerified(): boolean {
  if (isBotTraffic()) return false;
  if (humanVerified) return true;
  try {
    humanVerified = sessionStorage.getItem(HUMAN_VERIFIED_KEY) === "1";
  } catch {
    humanVerified = false;
  }
  return humanVerified;
}

function confirmHuman(): void {
  if (humanVerified || isBotTraffic() || !hasRealBrowserSignals()) return;
  humanVerified = true;
  try {
    sessionStorage.setItem(HUMAN_VERIFIED_KEY, "1");
  } catch {
    /* segue sem persistência */
  }
  window.dispatchEvent(new CustomEvent(HUMAN_VERIFIED_EVENT));
}

/**
 * Exige interação nativa ou permanência ativa para liberar pixels e page views.
 * Eventos sintéticos de JavaScript (`isTrusted=false`) são ignorados.
 */
export function startHumanVerification(): void {
  if (verificationStarted || typeof window === "undefined" || isBotTraffic()) return;
  verificationStarted = true;

  if (isHumanVerified()) {
    queueMicrotask(() => window.dispatchEvent(new CustomEvent(HUMAN_VERIFIED_EVENT)));
    return;
  }

  const onInteraction = (event: Event) => {
    if (!event.isTrusted) return;
    confirmHuman();
    if (humanVerified) removeListeners();
  };
  const events: (keyof WindowEventMap)[] = ["pointerdown", "touchstart", "keydown", "scroll"];
  const removeListeners = () => events.forEach((name) => window.removeEventListener(name, onInteraction));
  events.forEach((name) => window.addEventListener(name, onInteraction, { passive: true }));

  // Leitores passivos reais continuam sendo contados após 3 s de permanência.
  window.setTimeout(() => {
    confirmHuman();
    if (humanVerified) removeListeners();
  }, 3_000);

}
